import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { site } from "../generated/content";
import { useLanguage } from "../context/LanguageContext";
import logoMark from "../assets/logo-mark.png";
import "../style/Header.css";

interface HeaderProps {
  /**
   * When true the header floats transparently over a full-bleed image and
   * only turns solid once that image has scrolled away. Pages without a hero
   * pass false and get a solid header from the start.
   */
  overHero?: boolean;
}

export default function Header({ overHero = false }: HeaderProps) {
  const { lang, setLang } = useLanguage();
  const [solid, setSolid] = useState(!overHero);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!overHero) {
      setSolid(true);
      return;
    }

    const hero = document.querySelector("[data-hero]");
    const header = headerRef.current;
    if (!hero || !header) return;

    // The threshold follows the header's real height, so changing the logo
    // size cannot silently shift where the swap happens.
    let observer: IntersectionObserver | undefined;

    const observe = () => {
      observer?.disconnect();
      observer = new IntersectionObserver(
        ([entry]) => setSolid(!entry.isIntersecting),
        { rootMargin: `-${Math.round(header.offsetHeight)}px 0px 0px 0px`, threshold: 0 }
      );
      observer.observe(hero);
    };

    observe();
    const resizeObserver = new ResizeObserver(observe);
    resizeObserver.observe(header);

    return () => {
      observer?.disconnect();
      resizeObserver.disconnect();
    };
  }, [overHero]);

  return (
    <header ref={headerRef} className={`site-header${solid ? " is-solid" : ""}`}>
      <Link
        className="site-header__brand"
        to="/"
        aria-label={`${site.companyName} — ana sayfa`}
        style={{ "--logo-url": `url(${logoMark})` } as React.CSSProperties}
      />

      <nav className="site-header__lang" aria-label="Dil">
        <button type="button" onClick={() => setLang("tr")} aria-current={lang === "tr"}>
          TR
        </button>
        <button type="button" onClick={() => setLang("en")} aria-current={lang === "en"}>
          EN
        </button>
      </nav>
    </header>
  );
}
